import {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { MdOutlineImageNotSupported } from 'react-icons/md';

import { useSidebar } from '../../hooks/useSidebar';
import {
  Container,
  Logo,
  LogoPlaceholder,
  LogoText,
  MenuIcon,
  NavLogoContainer,
  OverlayToggleToMobile,
  SpaceAccordion,
  SpaceToEnd,
  ToggleSidebar,
  ToggleSidebarContainerToMobile,
} from './styles';

interface ISidebarProps {
  children?: ReactNode;
  footerChildren?: ReactNode;
  isShrunken?: boolean;
  logoRedirectTo?: string;
  logoSrc?: string;
  logoText?: string;
}

export const Sidebar = ({
  children,
  footerChildren,
  isShrunken = false,
  logoRedirectTo = '',
  logoSrc,
  logoText = '',
}: ISidebarProps): JSX.Element => {
  const { accordionSidebarValue, handleAccordionSidebar, handleShrinkSidebar } =
    useSidebar();
  const normalizedLogoSrc = logoSrc?.trim() || undefined;
  const normalizedLogoText = logoText.trim();
  const [hasLogoLoadError, setHasLogoLoadError] = useState(false);
  const showLogo = Boolean(normalizedLogoSrc) && !hasLogoLoadError;

  useEffect(() => {
    setHasLogoLoadError(false);
  }, [normalizedLogoSrc]);

  const handleLogoError = () => {
    setHasLogoLoadError(true);
  };

  const handleAccordionSidebarChange = useCallback(
    (value: string) => {
      handleAccordionSidebar(value);

      const isMobile = window.matchMedia('(max-width: 600px)').matches;
      handleShrinkSidebar(isMobile ? true : false);
    },
    [handleAccordionSidebar, handleShrinkSidebar]
  );

  const handleToggleIsShrunken = useCallback(
    (e: MouseEvent<HTMLElement>, preventDefault = false) => {
      preventDefault && e.preventDefault();
      e.stopPropagation();

      if (window.matchMedia('(max-width: 600px)').matches) {
        const isShrunkenToMobile = false;
        handleShrinkSidebar(isShrunkenToMobile);
      }
    },
    [handleShrinkSidebar]
  );

  const handleToggleIsShrunkenAndPrevent = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      handleToggleIsShrunken(e, true);
    },
    [handleToggleIsShrunken]
  );

  return (
    <>
      <OverlayToggleToMobile
        $isShrunken={isShrunken}
        onClick={handleToggleIsShrunken}
      />
      <Container $isShrunken={isShrunken} data-cy="sidebar">
        <NavLogoContainer
          $isShrunken={isShrunken}
          aria-label={
            normalizedLogoText || (showLogo ? 'Logo' : 'No logo uploaded')
          }
          data-cy="navLink-logo"
          onClick={handleToggleIsShrunken}
          to={logoRedirectTo}
        >
          {showLogo ? (
            <Logo src={normalizedLogoSrc} alt="" onError={handleLogoError} />
          ) : (
            <LogoPlaceholder
              data-cy="navLink-logo-placeholder"
              title="No logo uploaded"
              aria-hidden
            >
              <MdOutlineImageNotSupported size={48} aria-hidden />
            </LogoPlaceholder>
          )}
          {normalizedLogoText ? (
            <LogoText $isShrunken={isShrunken} aria-hidden>
              {normalizedLogoText}
            </LogoText>
          ) : null}
          <ToggleSidebarContainerToMobile $isShrunken={isShrunken}>
            <ToggleSidebar
              data-cy="button-menu"
              onClick={handleToggleIsShrunkenAndPrevent}
              type="button"
            >
              <MenuIcon />
            </ToggleSidebar>
          </ToggleSidebarContainerToMobile>
        </NavLogoContainer>
        <SpaceAccordion
          type="single"
          collapsible
          onValueChange={handleAccordionSidebarChange}
          data-cy="extra-nav-link-container"
          value={accordionSidebarValue ?? ''}
        >
          {children}

          <SpaceToEnd>{footerChildren}</SpaceToEnd>
        </SpaceAccordion>
      </Container>
    </>
  );
};
